import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  SCHEDULABLE_TRAINERS,
  scheduleRuleForSave,
  trainerScheduleApi,
  type TrainerKind,
  type TrainerScheduleRule,
} from '@/api/trainerSchedule';
import { AudiencePicker } from '@/components/curator/AudiencePicker';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { dayValue } from '@/lib/day';
import { humanizeError } from '@/lib/errors';
import { Modal } from '@/components/ui/Modal';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MAdd, MBlock, MEmpty, MRow, MTag, MTitle } from '@/components/mobile/MobileKit';

/**
 * Расписание тренажёров.
 *
 * ⚠️ ЗАДАЁТСЯ ПРАВИЛО, А НЕ ЗАПИСЬ НА КАЖДЫЙ ДЕНЬ — как у списка дел.
 * «Спикинг по пн/ср/пт до конца потока» это одна строка.
 *
 * ⚠️ РАСПИСАНИЕ НЕ ОТКРЫВАЕТ ЗАКРЫТОГО ТАРИФОМ. Чего нет в тарифе, того ученик
 * не увидит ни в какой день.
 *
 * ⚠️ ПУСТОЕ РАСПИСАНИЕ — «ЗАКРЫТО», А НЕ «ДОСТУПНО ВСЕГДА». Так сервер работает с
 * 07.09.2026 (shared/trainerschedule: OpenWithSchedule), а с 13.10.2026 и пробный
 * экзамен — первый тоже — идёт только в окне куратора (docs/CONTRACTS/ai-skills.md).
 * Прежняя надпись обещала обратное и запутывала куратора.
 */
export function CuratorTrainerSchedulePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<TrainerScheduleRule> | null>(null);
  const [sheetFor, setSheetFor] = useState<TrainerScheduleRule | null>(null);
  const isMobile = useIsMobile();
  const ask = useConfirm();

  const dayNames = [
    t('weekdaysShort.mon'), t('weekdaysShort.tue'), t('weekdaysShort.wed'),
    t('weekdaysShort.thu'), t('weekdaysShort.fri'), t('weekdaysShort.sat'), t('weekdaysShort.sun'),
  ];

  const list = useQuery({ queryKey: ['curator', 'trainer-schedule'], queryFn: trainerScheduleApi.list });

  const save = useMutation({
    mutationFn: (draft: Partial<TrainerScheduleRule>) => {
      const rule = scheduleRuleForSave(draft);
      return rule.id ? trainerScheduleApi.update(rule.id, rule) : trainerScheduleApi.create(rule);
    },
    onSuccess: async () => {
      setEditing(null);
      await qc.invalidateQueries({ queryKey: ['curator', 'trainer-schedule'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => trainerScheduleApi.remove(id),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['curator', 'trainer-schedule'] }),
  });

  const rules = list.data ?? [];
  const draft = editing;
  const setDraft = (patch: Partial<TrainerScheduleRule>) => setEditing((d) => ({ ...(d ?? {}), ...patch }));

  const toggleWeekday = (day: number) => {
    const current = draft?.weekdays ?? [];
    setDraft({ weekdays: current.includes(day) ? current.filter((d) => d !== day) : [...current, day] });
  };

  // У пробного экзамена дни недели не спрашиваем: он задаётся окном «с и по».
  const isMock = draft?.trainer === 'mock';

  const startNew = () => setEditing({ trainer: 'speaking', audience_type: 'group', weekdays: [], is_active: true });
  const saveButton = draft && (
    <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
      {t('common.save')}
    </Button>
  );
  const cancelButton = (
    <Button variant="ghost" onClick={() => setEditing(null)}>
      {t('common.cancel')}
    </Button>
  );

  // Поля формы — одни на компьютер и телефон: на компьютере под списком, на телефоне окном во весь экран (правило 3).
  const fields = draft && (
        <div className="space-y-5">
          <div>
            <p className="mb-2 text-sm font-semibold text-on-surface">{t('trainerSchedule.whichTrainer')}</p>
            <div className="flex flex-wrap gap-2">
              {SCHEDULABLE_TRAINERS.map((trainer: TrainerKind) => (
                <Button
                  key={trainer}
                  variant={draft.trainer === trainer ? 'primary' : 'ghost'}
                  onClick={() => setDraft({ trainer })}
                >
                  {t(`trainerSchedule.trainer_${trainer}`)}
                </Button>
              ))}
            </div>
          </div>

          <AudiencePicker
            value={draft}
            onChange={(patch) => setDraft(patch)}
            labelPrefix="trainerSchedule"
          />

          {/* Дни недели — только у речи, письма и спринта. У пробного экзамена
              своё правило: окно «с и по», а не повторение по дням. */}
          {!isMock && (
            <div>
              <p className="mb-2 text-sm font-semibold text-on-surface">{t('trainerSchedule.days')}</p>
              <div className="flex flex-wrap gap-2">
                {dayNames.map((name, i) => {
                  const day = i + 1; // 1 — понедельник … 7 — воскресенье
                  const on = (draft.weekdays ?? []).includes(day);
                  return (
                    <Button key={day} variant={on ? 'primary' : 'ghost'} onClick={() => toggleWeekday(day)}>
                      {name}
                    </Button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-on-surface-variant">{t('trainerSchedule.daysHint')}</p>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block font-semibold text-on-surface">{t('trainerSchedule.opensOn')}</span>
              <input
                type="date"
                className="w-full rounded-xl border border-outline-variant bg-surface px-3 py-2"
                value={dayValue(draft.opens_on)}
                onChange={(e) => setDraft({ opens_on: e.target.value || null })}
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-semibold text-on-surface">{t('trainerSchedule.closesOn')}</span>
              <input
                type="date"
                className="w-full rounded-xl border border-outline-variant bg-surface px-3 py-2"
                value={dayValue(draft.closes_on)}
                onChange={(e) => setDraft({ closes_on: e.target.value || null })}
              />
            </label>
          </div>
          <p className="text-xs text-on-surface-variant">
            {isMock ? t('trainerSchedule.mockHint') : t('trainerSchedule.windowHint')}
          </p>

          <label className="flex min-h-11 items-center gap-2 text-sm text-on-surface">
            <input
              type="checkbox"
              checked={draft.is_active ?? true}
              onChange={(e) => setDraft({ is_active: e.target.checked })}
            />
            {t('trainerSchedule.active')}
          </label>

          {save.error && <p className="text-sm text-error">{humanizeError(save.error)}</p>}
        </div>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.14) ─────────────────────────
  // «Удалить» на телефоне — с подтверждением (лист «⋯» → окно приложения); на компьютере его нет и так остаётся (§4.14).
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('trainerSchedule.title')}
          subtitle={t('trainerSchedule.subtitle')}
          right={<MAdd label={t('trainerSchedule.add')} onClick={startNew} />}
        />
        <p className="px-0.5 text-[12px] text-on-surface-variant">{t('trainerSchedule.note')}</p>
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : rules.length === 0 ? (
          <MEmpty icon="schedule" title={t('trainerSchedule.empty')} />
        ) : (
          <MBlock>
            {rules.map((rule, i) => (
              <MRow
                key={rule.id}
                wrap
                onClick={() => setEditing(rule)}
                title={t(`trainerSchedule.trainer_${rule.trainer}`)}
                meta={`${t(`trainerSchedule.audience_${rule.audience_type}`)} · ${summarize(rule, dayNames, t)}`}
                dim={!rule.is_active}
                aside={
                  <span className="flex flex-none items-center gap-1">
                    {!rule.is_active && <MTag tone="muted">{t('trainerSchedule.paused')}</MTag>}
                    <button
                      type="button"
                      onClick={() => setSheetFor(rule)}
                      aria-label={t('nav.more')}
                      className="grid h-11 w-11 place-items-center rounded-full text-on-surface-variant"
                    >
                      <span className="material-symbols-outlined text-[20px]">more_horiz</span>
                    </button>
                  </span>
                }
                last={i === rules.length - 1}
              />
            ))}
          </MBlock>
        )}
        <ActionSheet
          open={!!sheetFor}
          onClose={() => setSheetFor(null)}
          title={sheetFor ? t(`trainerSchedule.trainer_${sheetFor.trainer}`) : undefined}
          actions={
            sheetFor
              ? [
                  { label: t('common.edit'), icon: 'edit', onSelect: () => setEditing(sheetFor) },
                  {
                    label: t('common.delete'),
                    icon: 'delete',
                    danger: true,
                    onSelect: async () => {
                      const trainer = t(`trainerSchedule.trainer_${sheetFor.trainer}`);
                      if (await ask({ message: t('staffMobile.trainerRuleRemoveConfirm', { trainer }), danger: true })) {
                        remove.mutate(sheetFor.id);
                      }
                    },
                  },
                ]
              : []
          }
        />
        <Modal
          open={!!draft}
          onClose={() => setEditing(null)}
          title={t('trainerSchedule.whichTrainer')}
          phoneFull
          footer={<div className="grid grid-cols-2 gap-2">{cancelButton}{saveButton}</div>}
        >
          {fields}
        </Modal>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('trainerSchedule.title')} subtitle={t('trainerSchedule.subtitle')} />

      <Card className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-on-surface-variant">{t('trainerSchedule.note')}</p>
          <Button onClick={startNew}>{t('trainerSchedule.add')}</Button>
        </div>

        {list.isLoading && <p className="text-sm text-on-surface-variant">…</p>}
        {!list.isLoading && rules.length === 0 && (
          <p className="text-sm text-on-surface-variant">{t('trainerSchedule.empty')}</p>
        )}

        <div className="space-y-2">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className="flex flex-wrap items-center gap-3 rounded-2xl bg-surface-container px-4 py-3"
            >
              <span className="font-semibold text-on-surface">{t(`trainerSchedule.trainer_${rule.trainer}`)}</span>
              <span className="rounded-full bg-surface-container-high px-2.5 py-0.5 text-[11px] text-on-surface-variant">
                {t(`trainerSchedule.audience_${rule.audience_type}`)}
              </span>
              <span className="text-sm text-on-surface-variant">{summarize(rule, dayNames, t)}</span>
              {!rule.is_active && (
                <span className="text-[11px] font-semibold text-on-surface-variant">
                  {t('trainerSchedule.paused')}
                </span>
              )}
              <div className="ml-auto flex gap-2">
                <Button variant="ghost" onClick={() => setEditing(rule)}>
                  {t('common.edit')}
                </Button>
                <Button variant="ghost" onClick={() => remove.mutate(rule.id)}>
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {draft && (
        <Card className="space-y-5">
          {fields}
          <div className="flex gap-2">
            {saveButton}
            {cancelButton}
          </div>
        </Card>
      )}
    </div>
  );
}

/** Короткая понятная строка: «пн, ср, пт» или «10.09 — 14.09». */
function summarize(
  rule: TrainerScheduleRule,
  dayNames: string[],
  t: (k: string) => string,
): string {
  const parts: string[] = [];
  if (rule.weekdays?.length) {
    parts.push([...rule.weekdays].sort((a, b) => a - b).map((d) => dayNames[d - 1]).join(', '));
  }
  const from = rule.opens_on ? human(rule.opens_on) : null;
  const to = rule.closes_on ? human(rule.closes_on) : null;
  if (from && to) parts.push(`${from} — ${to}`);
  else if (from) parts.push(`${t('trainerSchedule.fromDate')} ${from}`);
  else if (to) parts.push(`${t('trainerSchedule.toDate')} ${to}`);
  return parts.length ? parts.join(' · ') : t('trainerSchedule.always');
}

function human(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}.${m}` : iso;
}
